import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

const i18n = defineMessages({
	groupOverflowLabel: {
		id: 'team-presence.group.overflow.label',
		defaultMessage: 'Overflow menu',
		description: 'Accessible label for presence overflow menu button',
	},
});

// Base styles for more indicator button - margin will be conditionally applied based on feature gate
const moreIndicatorButtonBaseStyle = xcss({
	minWidth: '28px',
	padding: 'space.0',
	marginInline: 'space.0',
	marginBlock: 'space.025',
	backgroundColor: 'color.background.neutral.subtle',
	borderRadius: 'radius.small',
	':focus-visible': {
		outlineColor: 'color.border.focused',
		outlineStyle: 'solid',
		outlineWidth: 'border.width.focused',
		outlineOffset: 'space.050',
	},
});

// Legacy margin styles (used when platform spacing fix is disabled)
const moreIndicatorButtonLegacyMarginStyle = xcss({
	marginInline: 'space.025',
});

const moreIndicatorStyle = xcss({
	padding: 'space.075',
	font: 'font.heading.xxsmall',
	color: 'color.text.subtle',
	backgroundColor: 'color.background.accent.gray.subtler',
	borderRadius: 'radius.full',
	':hover': {
		backgroundColor: 'color.background.accent.gray.subtler.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.gray.subtler.pressed',
	},
});

const moreIndicatorActiveStyle = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

export function renderMoreIndicator(isOpen: boolean, count: number) {
	return (_: any, props: any) => {
		return <CorePresenceMoreIndicator count={count} isOpen={isOpen} {...props} />;
	};
}

export type CorePresenceMoreIndicatorProps = {
	count: number;
	isOpen: boolean;
	onClick?: () => void;
	testId?: string;
};

export const CorePresenceMoreIndicator = ({
	count,
	isOpen,
	onClick,
	testId,
}: CorePresenceMoreIndicatorProps) => {
	const { formatMessage } = useIntl();

	return (
		<Pressable
			data-vc="presence-overflow"
			aria-label={formatMessage(i18n.groupOverflowLabel)}
			onClick={onClick}
			testId={testId}
			xcss={[
				moreIndicatorButtonBaseStyle,
				!fg('platform-avatar-group-spacing-fix') && moreIndicatorButtonLegacyMarginStyle,
			]}
		>
			<Box xcss={[moreIndicatorStyle, isOpen && moreIndicatorActiveStyle]}>{count}</Box>
		</Pressable>
	);
};
